import React, { useState, useRef, useContext } from 'react';
import {
    Box,
    Button,
    Text,
    VStack,
    HStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Progress,
    Badge,
    Icon,
    useToast,
    Grid,
    GridItem,
    Tag,
    TagLabel,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
} from '@chakra-ui/react';
import { FiUploadCloud, FiFile, FiX, FiInfo, FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';
import ThemeContext from '../context/ThemeContext';

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

function UploadYourFile({ setUser, token }) {
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [noOfFileUploads, setNoOfFileUploads] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showRequiredFields, setShowRequiredFields] = useState(false);
    const [uploadedData, setUploadedData] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);
    const toast = useToast();
    const { themeData } = useContext(ThemeContext);

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Theme-aware colors
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.300';
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'blue.50';
    const tableBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.50';
    const tableHeaderBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : 'blue.50';
    const tableStripedBg = isDarkTheme ? 'rgba(66, 153, 225, 0.05)' : 'gray.50';
    const accordionBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'blue.50';
    const accordionHoverBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : 'blue.100';
    const uploadBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.4)' : 'gray.300';
    const uploadHoverBg = isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.50';
    const uploadActiveBorderColor = isDarkTheme ? accentColor : 'blue.400';
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;
    const progressBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.100';
    const categoryCardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.3)' : 'white';

    // Required fields organized by category
    const requiredFields = {
        'Customer Identification & Demographics': [
            { name: 'customer_id', description: 'Unique customer identifier' },
            { name: 'country_iso', description: 'Country code (3-letter ISO)' },
            { name: 'age', description: 'Customer age' },
        ],
        'Risk & Knowledge Assessment': [
            { name: 'risk_profile', description: 'Risk tolerance (1-10 scale)' },
            { name: 'knowledge_quiz_score', description: 'Financial knowledge score (0-100)' },
            { name: 'experience_years', description: 'Years of investment experience' }
        ],
        'Portfolio Allocation': [
            { name: 'cash_pct', description: 'Cash percentage (0-100)' },
            { name: 'etf_pct', description: 'ETF percentage (0-100)' },
            { name: 'single_stock_pct', description: 'Individual stock percentage (0-100)' },
        ],
        'Trading Behavior': [
            { name: 'days_since_last_trade', description: 'Days since last trade' },
            { name: 'stop_loss_sell_cnt', description: 'Stop-loss sell count' },
            { name: 'days_since_loss_sell', description: 'Days since last loss realization' },
            { name: 'leverage_ratio', description: 'Leverage usage ratio' },
        ],
        'Investment Patterns': [
            { name: 'home_country_weight_pct', description: 'Home country allocation (0-100)' },
            { name: 'trade_turnover_30d_pct_peer', description: 'Trading turnover vs peers (0-100)' },
            { name: 'anchor_price_gap_pct', description: 'Price gap from anchor price' },
        ],
        'Order Types & Social': [
            { name: 'limit_orders_at_purchase_px', description: 'Limit order usage (boolean)' },
            { name: 'social_hype_score', description: 'Social media influence (0-100)' },
            { name: 'days_since_hype_peak', description: 'Days since social hype peak' },
        ],
        'News & Sentiment': [
            { name: 'positive_news_sentiment_7d', description: 'News sentiment (-1 to 1)' },
            { name: 'news_clicks_7d', description: 'News engagement count' }
        ],
        'Gain/Loss Behavior': [
            { name: 'gain_takes', description: 'Profit-taking count' },
            { name: 'loss_takes', description: 'Loss realization count' },
        ],
        'Portfolio Management': [
            { name: 'rebalances_ytd', description: 'Rebalancing frequency' },
            { name: 'num_sub_accounts', description: 'Number of sub-accounts' },
            { name: 'averaging_down_trades', description: 'Dollar-cost averaging trades' },
            { name: 'days_since_last_average_down', description: 'Days since last DCA' },
            { name: 'max_drawdown_pct', description: 'Maximum drawdown percentage (-100 to 0)' },
        ]
    };

    // Upload file to API
    const uploadFileToAPI = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            setIsUploading(true);
            setUploadProgress(0);
            setShowPreview(false);
            setUploadError(null);

            const response = await axios.post(`${API_BASE_URL}/content/upload-file`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                },
            });

            // Handle response
            const data = response.data;
            setUploadedData(data);

            // Check if upload was successful or failed
            if (data.status === 'failed' && data.preview?.error) {
                setUploadError(data.preview.error);
                setShowPreview(true);
                toast({
                    title: 'Upload validation failed',
                    description: data.preview.error,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                await checkUserUploadCount();
            } else if (data.preview?.preview_rows && data.preview.preview_rows.length > 0) {
                setShowPreview(true);
                toast({
                    title: 'File uploaded successfully',
                    description: `Preview loaded with ${data.preview.preview_rows.length} rows.`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                await checkUserUploadCount();
            }

            return data;
        } catch (error) {
            console.error('Upload error:', error);

            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                'Failed to upload file. Please try again.';

            setUploadError(errorMessage);
            toast({
                title: 'Upload failed',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });

            setFiles([]);
            setUploadProgress(0);

            throw error;
        } finally {
            setIsUploading(false);
        }
    };

    // File upload handler
    const handleFileUpload = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        const validFiles = selectedFiles.filter(
            (file) =>
                file.name.endsWith('.csv') ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls')
        );

        if (validFiles.length !== selectedFiles.length) {
            toast({
                title: 'Invalid file type',
                description: 'Please upload only CSV or Excel files',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        if (validFiles.length > 0) {
            const fileToUpload = validFiles[0];
            setFiles([fileToUpload]);
            await uploadFileToAPI(fileToUpload);
        }
    };

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = droppedFiles.filter(
            (file) =>
                file.name.endsWith('.csv') ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls')
        );

        if (validFiles.length > 0) {
            const fileToUpload = validFiles[0];
            setFiles([fileToUpload]);
            await uploadFileToAPI(fileToUpload);
        }
    };

    // Remove all files
    const handleRemoveAll = async () => {
        try {
            setIsDeleting(true);
            await axios.get(`${API_BASE_URL}/content/remove-latest-uploaded-file`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            toast({
                title: 'File deleted',
                description: 'The uploaded file was successfully removed from the server.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            //Call check-user again to refresh noOfFileUploads
            await checkUserUploadCount();
        } catch (error) {
            console.error('Error removing file:', error);

            toast({
                title: 'Failed to delete file',
                description:
                    error.response?.data?.message ||
                    'An error occurred while deleting the file.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsDeleting(false);
            // Reset frontend state
            setFiles([]);
            setShowPreview(false);
            setUploadProgress(0);
            setUploadedData(null);
            setUploadError(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Verify and continue handler
    const handleVerifyAndContinue = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.status === 200) {
                setUser(res.data);
                toast({
                    title: 'Verification successful',
                    description: 'Redirecting to your dashboard...',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Unexpected response',
                    description: 'Please try again later.',
                    status: 'warning',
                    duration: 2000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Verification failed:', error);
            toast({
                title: 'Verification failed',
                description: 'Unable to verify your upload.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const checkUserUploadCount = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 200 && res.data) {
                setNoOfFileUploads(res.data.noOfFileUploads);
                // setUser(res.data);
            }
        } catch (error) {
            console.error('Error checking user upload count:', error);
        }
    };

    // Format value for display
    const formatValue = (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') return value.toLocaleString();
        return value.toString();
    };

    return (
        <Box maxW="100%" mx="auto" p={{ base: 4, sm: 6, md: 8 }} w="full">
            <Box 
                bg={cardBg} 
                borderRadius="xl" 
                boxShadow="md"
                p={8} 
                w="full"
                borderWidth="1px"
                borderColor={borderColor}
            >
                <Text fontSize={{ base: 'xl', sm: '2xl' }} fontWeight="bold" mb={1} color={headingTextColor}>
                    Upload your customers file
                </Text>
                <Text fontSize={{ base: 'sm', sm: 'md' }} color={mutedTextColor} mb={6}>
                    Import a CSV or Excel file to generate product recommendations per customer.
                </Text>

                {/* Required Fields Info */}
                {noOfFileUploads !== 1 && (
                    <Box mb={6}>
                        <Accordion allowToggle>
                            <AccordionItem border="none">
                                <AccordionButton
                                    onClick={() => setShowRequiredFields(!showRequiredFields)}
                                    bg={accordionBg}
                                    borderRadius="lg"
                                    p={4}
                                    _hover={{ bg: accordionHoverBg }}
                                    _expanded={{ bg: accordionHoverBg }}
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <HStack flex="1" textAlign="left">
                                        <Icon as={FiInfo} color={isDarkTheme ? accentColor : primaryColor} />
                                        <VStack align="start" spacing={0}>
                                            <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>
                                                Required Fields (28 total)
                                            </Text>
                                            <Text fontSize="xs" color={bodyTextColor}>
                                                Click to view all required columns for your CSV/Excel file
                                            </Text>
                                        </VStack>
                                    </HStack>
                                    <AccordionIcon color={isDarkTheme ? accentColor : primaryColor} />
                                </AccordionButton>
                                <AccordionPanel pb={4} pt={6}>
                                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                                        {Object.entries(requiredFields).map(([category, fields]) => (
                                            <GridItem key={category}>
                                                <Box
                                                    bg={categoryCardBg}
                                                    borderWidth="1px"
                                                    borderColor={borderColor}
                                                    borderRadius="lg"
                                                    p={4}
                                                    height="full"
                                                >
                                                    <Text fontSize="sm" fontWeight="bold" color={headingTextColor} mb={3}>
                                                        {category}
                                                    </Text>
                                                    <VStack align="start" spacing={2}>
                                                        {fields.map((field) => (
                                                            <Box key={field.name} width="full">
                                                                <Tag
                                                                    size="sm"
                                                                    colorScheme="blue"
                                                                    variant="subtle"
                                                                    borderRadius="md"
                                                                    mb={1}
                                                                >
                                                                    <TagLabel fontSize="xs" fontWeight="medium">
                                                                        {field.name}
                                                                    </TagLabel>
                                                                </Tag>
                                                                <Text fontSize="xs" color={mutedTextColor} pl={1}>
                                                                    {field.description}
                                                                </Text>
                                                            </Box>
                                                        ))}
                                                    </VStack>
                                                </Box>
                                            </GridItem>
                                        ))}
                                    </Grid>

                                    {/* CSV Format Example */}
                                    <Box 
                                        mt={6} 
                                        p={4} 
                                        bg={tableBg} 
                                        borderRadius="lg"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <Text fontSize="sm" fontWeight="semibold" color={bodyTextColor} mb={2}>
                                            CSV Format Example:
                                        </Text>
                                        <Text fontSize="xs" color={mutedTextColor} fontFamily="monospace" whiteSpace="pre-wrap">
                                            {`customer_id,country_iso,age,risk_profile,...\nCUST001,US,35,6,...\nCUST002,UK,42,8,...`}
                                        </Text>
                                    </Box>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    </Box>
                )}


                {/* Upload Area */}
                {noOfFileUploads !== 1 && (
                    <Box
                        border="2px dashed"
                        borderColor={isUploading ? uploadActiveBorderColor : uploadBorderColor}
                        borderRadius="lg"
                        p={12}
                        textAlign="center"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        mb={4}
                        cursor={isUploading ? 'not-allowed' : 'pointer'}
                        transition="all 0.2s"
                        _hover={{ borderColor: uploadActiveBorderColor, bg: uploadHoverBg }}
                        onClick={() => !isUploading && fileInputRef.current?.click()}
                        maxW="full"
                        opacity={isUploading ? 0.6 : 1}
                        pointerEvents={isUploading ? 'none' : 'auto'}
                    >
                        {isUploading ? (
                            <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} thickness="4px" mb={4} />
                        ) : (
                            <Icon 
                                as={FiUploadCloud} 
                                boxSize={10} 
                                color={isDarkTheme ? accentColor : mutedTextColor} 
                                mb={4} 
                            />
                        )}
                        <Text fontSize="md" mb={2} color={strongTextColor} fontWeight="medium">
                            {isUploading ? 'Uploading...' : 'Drag & drop your CSV/XLSX here'}
                        </Text>
                        {!isUploading && (
                            <>
                                <Text fontSize="sm" color={mutedTextColor} mb={4}>
                                    or
                                </Text>
                                <Button 
                                    bg={primaryColor} 
                                    color="white" 
                                    size="md"
                                    _hover={{ bg: buttonHoverBg }}
                                >
                                    Browse files
                                </Button>
                                <Text fontSize="xs" color={mutedTextColor} mt={3}>
                                    Accepted: .csv, .xlsx
                                </Text>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                            disabled={isUploading}
                        />
                    </Box>
                )}

                {/* Info Box */}
                {noOfFileUploads !== 1 && (
                    <Box 
                        bg={infoBg} 
                        borderRadius="md" 
                        p={4} 
                        mb={4}
                        borderWidth="1px"
                        borderColor={borderColor}
                    >
                        <Text fontSize="sm" fontWeight="semibold" color={headingTextColor} mb={1}>
                            Make sure your file includes all 28 required fields
                        </Text>
                        <Text fontSize="xs" color={bodyTextColor}>
                            The file should contain columns for customer identification, demographics, portfolio composition, trading behavior, and more.
                        </Text>
                    </Box>
                )}


                {/* Declaration */}
                {uploadedData && !isUploading && !uploadError && (
                    <Alert
                        status="success"
                        variant="subtle"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        borderRadius="md"
                        py={6}
                        mb={6}
                        borderWidth="1px"
                        borderColor="green.300"
                    >
                        <AlertIcon boxSize="30px" mr={0} />
                        <AlertTitle mt={2} mb={1} fontSize="lg" fontWeight="bold" color={strongTextColor}>
                            File Uploaded Successfully
                        </AlertTitle>
                        <AlertDescription maxWidth="sm" fontSize="sm" color={bodyTextColor}>
                            Your file has been uploaded and saved successfully. You can preview or delete the file on this screen,
                            or visit the <Text as="span" fontWeight="bold">Datasets</Text> tab on your dashboard.
                        </AlertDescription>
                    </Alert>
                )}

                {/* Uploaded Files */}
                {files.length > 0 && (
                    <HStack spacing={4} mb={4} flexWrap="wrap">
                        {files.map((file, index) => (
                            <HStack
                                key={index}
                                bg={tableBg}
                                px={3}
                                py={2}
                                borderRadius="md"
                                spacing={2}
                                maxW="100%"
                                borderWidth="1px"
                                borderColor={borderColor}
                            >
                                <Icon as={FiFile} color={mutedTextColor} />
                                <Text fontSize="sm" fontWeight="medium" color={strongTextColor}>
                                    {file.name}
                                </Text>
                                <Text fontSize="xs" color={mutedTextColor}>
                                    • {(file.size / 1024).toFixed(0)}KB
                                </Text>
                            </HStack>
                        ))}
                    </HStack>
                )}

                {/* Upload Progress */}
                {isUploading && (
                    <Box mb={4}>
                        <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="medium" color={strongTextColor}>
                                Uploading file...
                            </Text>
                        </HStack>
                        <Progress
                            value={uploadProgress}
                            colorScheme="blue"
                            borderRadius="full"
                            size="sm"
                            hasStripe
                            isAnimated
                            bg={progressBg}
                            sx={{
                                '& > div': {
                                    backgroundColor: isDarkTheme ? accentColor : primaryColor
                                }
                            }}
                        />
                        <Text fontSize="xs" color={mutedTextColor} mt={2}>
                            Processing your file... This may take a few moments.
                        </Text>
                    </Box>
                )}

                {/* Error Alert */}
                {uploadError && (
                    <Alert 
                        status="error" 
                        borderRadius="md" 
                        mb={4}
                        borderWidth="1px"
                        borderColor="red.300"
                    >
                        <AlertIcon as={FiAlertCircle} />
                        <Box>
                            <AlertDescription fontSize="sm">
                                {uploadError}
                            </AlertDescription>
                        </Box>
                    </Alert>
                )}

                {/* File Info */}
                {uploadedData?.preview && !isUploading && (
                    <Box 
                        bg={tableBg} 
                        borderRadius="md" 
                        p={4} 
                        mb={4}
                        borderWidth="1px"
                        borderColor={borderColor}
                    >
                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                            <Box>
                                <Text fontSize="xs" color={labelTextColor}>Filename</Text>
                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                    {uploadedData.preview.filename}
                                </Text>
                            </Box>
                            <Box>
                                <Text fontSize="xs" color={labelTextColor}>Total Rows</Text>
                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                    {uploadedData.preview.total_rows}
                                </Text>
                            </Box>
                            <Box>
                                <Text fontSize="xs" color={labelTextColor}>Total Columns</Text>
                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                    {uploadedData.preview.total_columns}
                                </Text>
                            </Box>
                            <Box>
                                <Text fontSize="xs" color={labelTextColor}>File Size</Text>
                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                    {(uploadedData.preview.file_size / 1024).toFixed(2)} KB
                                </Text>
                            </Box>
                        </Grid>
                    </Box>
                )}



                {/* Action Buttons */}
                {files.length > 0 && !isUploading && (
                    <HStack spacing={3} mb={6}>
                        <Button
                            variant="outline"
                            size="md"
                            onClick={handleRemoveAll}
                            leftIcon={<FiX />}
                            borderColor={borderColor}
                            color={strongTextColor}
                            isLoading={isDeleting}
                            loadingText="Removing..."
                            _hover={{ bg: uploadHoverBg }}
                        >
                            Remove File
                        </Button>
                    </HStack>
                )}

                {/* Preview Section */}
                {showPreview && uploadedData?.preview?.preview_rows && !isUploading && (
                    <>
                        <Text fontSize="lg" fontWeight="semibold" mb={4} color={headingTextColor}>
                            Preview (first {uploadedData.preview.preview_rows.length} customers)
                        </Text>

                        <Box 
                            overflowX="auto" 
                            mb={6}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={borderColor}
                        >
                            <Table variant="simple" size="sm">
                                <Thead bg={tableHeaderBg}>
                                    <Tr>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>Customer ID</Th>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>Country</Th>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>Age</Th>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>Risk Profile</Th>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>Experience</Th>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>Cash %</Th>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>ETF %</Th>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>Stock %</Th>
                                        <Th color={labelTextColor} fontSize="xs" fontWeight="semibold" borderColor={borderColor}>Leverage</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {uploadedData.preview.preview_rows.map((row, index) => (
                                        <Tr 
                                            key={index} 
                                            bg={index % 2 === 1 ? tableStripedBg : 'transparent'}
                                            _hover={{ bg: uploadHoverBg }}
                                        >
                                            <Td borderColor={borderColor}>
                                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                    {formatValue(row.customer_id)}
                                                </Text>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Badge colorScheme="blue" fontSize="xs">
                                                    {formatValue(row.country_iso)}
                                                </Badge>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Text fontSize="sm" color={bodyTextColor}>{formatValue(row.age)}</Text>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Badge
                                                    colorScheme={row.risk_profile >= 7 ? 'red' : row.risk_profile >= 4 ? 'yellow' : 'green'}
                                                    fontSize="xs"
                                                >
                                                    {formatValue(row.risk_profile)}
                                                </Badge>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Text fontSize="sm" color={bodyTextColor}>{formatValue(row.experience_years)}</Text>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Text fontSize="sm" color={bodyTextColor}>{formatValue(row.cash_pct)}%</Text>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Text fontSize="sm" color={bodyTextColor}>{formatValue(row.etf_pct)}%</Text>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Text fontSize="sm" color={bodyTextColor}>{formatValue(row.single_stock_pct)}%</Text>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Text fontSize="sm" color={bodyTextColor}>{formatValue(row.leverage_ratio)}</Text>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>

                        {/* Bottom Action Buttons */}
                        <HStack spacing={3}>
                            <Button 
                                bg={primaryColor} 
                                color="white" 
                                size="md" 
                                onClick={handleVerifyAndContinue}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Continue
                            </Button>
                        </HStack>
                    </>
                )}
            </Box>
        </Box>
    );
}

export default UploadYourFile;